import React, { useState } from 'react';
import { Building2, Calendar, TrendingUp, Clock, Info, CalendarRange } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const EQUITY_TAX_TYPES = ['Equity', 'ETF', 'Equity Mutual Fund'];
const isEquityTax = (t) => EQUITY_TAX_TYPES.includes(t || 'Equity');

const fmt = (n) => (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => new Date(d).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
const holdingLabel = (days) => {
  if (days < 30) return `${days} days`;
  if (days < 365) return `${Math.floor(days / 30)} months`;
  return `${(days / 365).toFixed(2)} years`;
};

// Indian Financial Year (Apr-Mar) label for a given date, e.g. "FY 2024-25".
const getFY = (dateStr) => {
  const d = new Date(dateStr);
  const y = d.getFullYear();
  const startYear = d.getMonth() >= 3 ? y : y - 1; // April = month index 3
  return `FY ${startYear}-${String(startYear + 1).slice(-2)}`;
};

const GainsTable = ({ rows, headerClass, badgeClass }) => (
  <div className="rounded-lg border border-slate-200 overflow-hidden bg-white">
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow className={headerClass}>
            <TableHead className="font-semibold text-slate-700">Scrip</TableHead>
            <TableHead className="font-semibold text-slate-700">Portfolio</TableHead>
            <TableHead className="font-semibold text-slate-700">Type</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Qty</TableHead>
            <TableHead className="font-semibold text-slate-700">Purchase</TableHead>
            <TableHead className="font-semibold text-slate-700">Sell</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Holding</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Cost (₹)</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Sale Value (₹)</TableHead>
            <TableHead className="font-semibold text-slate-700 text-right">Capital Gain (₹)</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((t) => (
            <TableRow key={t.id} className="hover:bg-slate-50">
              <TableCell>
                <div className="font-semibold text-slate-900">{t.ticker}</div>
                <div className="text-xs text-slate-600">{t.shareName}</div>
              </TableCell>
              <TableCell className="text-xs text-slate-600">{t.portfolioName}</TableCell>
              <TableCell><Badge variant="outline" className="text-xs bg-slate-50">{t.instrumentType || 'Equity'}</Badge></TableCell>
              <TableCell className="text-right">{(t.quantity || 0).toLocaleString('en-IN', { maximumFractionDigits: 3 })}</TableCell>
              <TableCell>{fmtDate(t.purchaseDate)}</TableCell>
              <TableCell>{fmtDate(t.sellDate)}</TableCell>
              <TableCell className="text-right"><Badge variant="outline" className={badgeClass}>{holdingLabel(t.holdingPeriodDays)}</Badge></TableCell>
              <TableCell className="text-right">₹{fmt(t.totalCost)}</TableCell>
              <TableCell className="text-right font-semibold">₹{fmt(t.currentValue)}</TableCell>
              <TableCell className={`text-right font-bold ${t.profit >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>{t.profit >= 0 ? '+' : ''}₹{fmt(t.profit)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  </div>
);

const CapitalGainsReport = ({ transactions, entityInfo, portfolios = [] }) => {
  const currentDate = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const [selectedFY, setSelectedFY] = useState('all');
  const pmap = Object.fromEntries((portfolios || []).map((p) => [p.id, p.name]));

  // All realized (sold) transactions with derived tax fields + financial year.
  const allSold = transactions.filter((t) => t.status === 'sold').map((t) => {
    const days = Math.floor((new Date(t.sellDate) - new Date(t.purchaseDate)) / (1000 * 60 * 60 * 24));
    const equity = isEquityTax(t.instrumentType);
    const longTerm = equity ? days > 365 : days > 1095; // equity: 12mo, debt/other: 36mo
    return { ...t, holdingPeriodDays: days, equity, longTerm, fy: getFY(t.sellDate), portfolioName: pmap[t.portfolioId] || '—' };
  });

  const availableFYs = [...new Set(allSold.map((t) => t.fy))].sort().reverse();

  // Financial year-wise summary (across all years, independent of the filter).
  const fyGroups = {};
  allSold.forEach((t) => {
    const g = fyGroups[t.fy] || { fy: t.fy, eqLT: 0, eqST: 0, debt: 0, total: 0, count: 0 };
    if (t.equity && t.longTerm) g.eqLT += t.profit;
    else if (t.equity) g.eqST += t.profit;
    else g.debt += t.profit;
    g.total += t.profit;
    g.count += 1;
    fyGroups[t.fy] = g;
  });
  const fyRows = Object.values(fyGroups).sort((a, b) => b.fy.localeCompare(a.fy));

  // Detailed sections respect the selected FY filter.
  const sold = selectedFY === 'all' ? allSold : allSold.filter((t) => t.fy === selectedFY);
  const equityLT = sold.filter((t) => t.equity && t.longTerm);
  const equityST = sold.filter((t) => t.equity && !t.longTerm);
  const debtLT = sold.filter((t) => !t.equity && t.longTerm);
  const debtST = sold.filter((t) => !t.equity && !t.longTerm);

  const sum = (arr) => arr.reduce((a, t) => a + t.profit, 0);
  const equityLTGain = sum(equityLT);
  const equitySTGain = sum(equityST);
  const debtGain = sum(debtLT) + sum(debtST);
  const totalGain = equityLTGain + equitySTGain + debtGain;

  const gainCls = (v) => (v >= 0 ? 'text-emerald-600' : 'text-rose-600');

  return (
    <Card className="border-2 border-purple-200 shadow-xl bg-gradient-to-br from-white to-purple-50" data-testid="capital-gains-report">
      <CardHeader className="border-b-2 border-purple-200 bg-purple-50">
        <div className="flex justify-between items-start flex-wrap gap-4">
          <div>
            <CardTitle className="text-2xl font-bold text-slate-900 mb-2">Capital Gains Report</CardTitle>
            <div className="space-y-1 text-sm text-slate-600">
              <div className="flex items-center gap-2"><Building2 className="h-4 w-4" /><span className="font-semibold">{entityInfo.entityName}</span></div>
              <div>Portfolio ID: {entityInfo.portfolioId} | PAN: {entityInfo.panNumber}</div>
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /><span>Report Date: {currentDate}</span></div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-3">
            <Badge className="bg-purple-100 text-purple-700 text-sm px-3 py-1">{sold.length} Realized</Badge>
            <div className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4 text-purple-600" />
              <Select value={selectedFY} onValueChange={setSelectedFY}>
                <SelectTrigger className="w-44 bg-white" data-testid="cg-year-filter">
                  <SelectValue placeholder="Financial Year" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all" data-testid="cg-year-all">All Years</SelectItem>
                  {availableFYs.map((fy) => (
                    <SelectItem key={fy} value={fy} data-testid={`cg-year-${fy.replace(/\s/g, '-')}`}>{fy}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-6">
        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 p-4 bg-white rounded-lg border border-slate-200">
          <div>
            <p className="text-xs text-slate-600 mb-1">Total Capital Gains</p>
            <p className={`text-xl font-bold ${gainCls(totalGain)}`}>₹{fmt(totalGain)}</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Equity LTCG</p>
            <p className={`text-xl font-bold ${gainCls(equityLTGain)}`}>₹{fmt(equityLTGain)}</p>
            <p className="text-xs text-slate-500">{equityLT.length} txns (&gt;1yr)</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Equity STCG</p>
            <p className={`text-xl font-bold ${gainCls(equitySTGain)}`}>₹{fmt(equitySTGain)}</p>
            <p className="text-xs text-slate-500">{equityST.length} txns (≤1yr)</p>
          </div>
          <div>
            <p className="text-xs text-slate-600 mb-1">Debt / Other Gains</p>
            <p className={`text-xl font-bold ${gainCls(debtGain)}`}>₹{fmt(debtGain)}</p>
            <p className="text-xs text-slate-500">{debtLT.length + debtST.length} txns</p>
          </div>
        </div>

        {/* Financial Year-wise Summary */}
        {fyRows.length > 0 && (
          <div className="mb-6" data-testid="cg-yearwise-summary">
            <h2 className="text-lg font-bold text-slate-900 mb-3 flex items-center gap-2"><CalendarRange className="h-5 w-5 text-purple-600" /> Financial Year-wise Summary</h2>
            <div className="rounded-lg border border-purple-200 overflow-hidden bg-white">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-purple-50 hover:bg-purple-50">
                      <TableHead className="font-semibold text-slate-700">Financial Year</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Equity LTCG (₹)</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Equity STCG (₹)</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Debt / Other (₹)</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Total Gain (₹)</TableHead>
                      <TableHead className="font-semibold text-slate-700 text-right">Txns</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fyRows.map((g) => (
                      <TableRow
                        key={g.fy}
                        className={`cursor-pointer hover:bg-purple-50 ${selectedFY === g.fy ? 'bg-purple-50' : ''}`}
                        onClick={() => setSelectedFY(g.fy)}
                        data-testid={`cg-fy-row-${g.fy.replace(/\s/g, '-')}`}
                      >
                        <TableCell className="font-semibold text-slate-900">{g.fy}</TableCell>
                        <TableCell className={`text-right font-medium ${gainCls(g.eqLT)}`}>₹{fmt(g.eqLT)}</TableCell>
                        <TableCell className={`text-right font-medium ${gainCls(g.eqST)}`}>₹{fmt(g.eqST)}</TableCell>
                        <TableCell className={`text-right font-medium ${gainCls(g.debt)}`}>₹{fmt(g.debt)}</TableCell>
                        <TableCell className={`text-right font-bold ${gainCls(g.total)}`}>{g.total >= 0 ? '+' : ''}₹{fmt(g.total)}</TableCell>
                        <TableCell className="text-right text-slate-600">{g.count}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-2">Tip: click any financial year row to filter the detailed tables below.</p>
          </div>
        )}

        {/* Tax info */}
        <div className="mb-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
          <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2"><Info className="h-4 w-4" /> Tax Information (India)</h4>
          <div className="text-sm text-blue-700 space-y-1">
            <p>• <strong>Equity / Equity MF / ETF</strong>: &gt;12 months = LTCG (12.5% on gains above ₹1.25L); ≤12 months = STCG (20%).</p>
            <p>• <strong>Debt MF (bought after Apr 2023)</strong>: gains taxed at your <strong>income-tax slab</strong> rate regardless of holding period.</p>
            <p>• <strong>Bonds / Other debt</strong>: &gt;36 months treated as Long Term, else Short Term (taxed at slab).</p>
            <p className="text-xs text-blue-600 mt-2">*Rates are indicative for FY24-25. Please consult a qualified tax advisor.</p>
          </div>
        </div>

        {/* Detailed section heading with active filter */}
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-lg font-bold text-slate-900">Detailed Realized Gains</h2>
          <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-300">
            {selectedFY === 'all' ? 'All Years' : selectedFY}
          </Badge>
        </div>

        {/* Equity section */}
        {(equityLT.length > 0 || equityST.length > 0) && (
          <div className="mb-8">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Equity Capital Gains</h2>
            {equityLT.length > 0 && (
              <div className="mb-5">
                <h3 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-green-600" /> Long-term (LTCG)</h3>
                <GainsTable rows={equityLT} headerClass="bg-green-50 hover:bg-green-50" badgeClass="bg-green-50 text-green-700 border-green-300" />
              </div>
            )}
            {equityST.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-orange-600" /> Short-term (STCG)</h3>
                <GainsTable rows={equityST} headerClass="bg-orange-50 hover:bg-orange-50" badgeClass="bg-orange-50 text-orange-700 border-orange-300" />
              </div>
            )}
          </div>
        )}

        {/* Debt / Other section */}
        {(debtLT.length > 0 || debtST.length > 0) && (
          <div className="mb-4">
            <h2 className="text-lg font-bold text-slate-900 mb-3">Debt / Other Capital Gains</h2>
            {debtLT.length > 0 && (
              <div className="mb-5">
                <h3 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-blue-600" /> Long-term (&gt;36 months)</h3>
                <GainsTable rows={debtLT} headerClass="bg-blue-50 hover:bg-blue-50" badgeClass="bg-blue-50 text-blue-700 border-blue-300" />
              </div>
            )}
            {debtST.length > 0 && (
              <div>
                <h3 className="text-base font-semibold text-slate-800 mb-2 flex items-center gap-2"><Clock className="h-4 w-4 text-slate-600" /> Short-term (slab rate)</h3>
                <GainsTable rows={debtST} headerClass="bg-slate-100 hover:bg-slate-100" badgeClass="bg-slate-100 text-slate-700 border-slate-300" />
              </div>
            )}
          </div>
        )}

        {sold.length === 0 && (
          <div className="text-center py-12 text-slate-500" data-testid="cg-empty-state">
            <p className="text-lg">{allSold.length === 0 ? 'No realized capital gains' : `No realized gains for ${selectedFY}`}</p>
            <p className="text-sm mt-1">{allSold.length === 0 ? 'Sell some positions to see the capital gains report' : 'Try selecting a different financial year'}</p>
          </div>
        )}

        <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <p className="text-xs text-slate-600">
            <strong>Important:</strong> This report shows realized gains from sold positions, classified by instrument type and financial year (Apr–Mar). Equity uses the 12-month LTCG rule; debt/other uses 36 months (note: debt MFs bought after Apr-2023 are taxed at slab regardless). Consult a qualified tax professional for filing.
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default CapitalGainsReport;
